/**
 * Join step six: the last look before the draft becomes a participation.
 *
 * The submit is the only write of the wizard and it accepts either a first join
 * or an update of an existing one, because the API upserts. The draft is cleared
 * only after the server accepted it: a failed request must not lose the answers.
 */

import { Link, useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'

import { isGcj02Coordinate } from '@shared/validation'
import type { JoinEventInput } from '@shared/types'

import { ErrorAlert, LoadingBlock } from '../../../components/layout/states'
import { WizardFooter } from '../../../components/layout/wizard'
import { Alert, AlertDescription } from '../../../components/ui/alert'
import { Button } from '../../../components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card'
import { Chip, ChipGroup } from '../../../components/ui/chip'
import { Stat, StatGrid } from '../../../components/ui/primitives'
import {
  useDepartureOptions,
  useJoinEvent,
  useMyParticipant,
} from '../../../hooks/use-shared'
import { mutationErrorText } from '../../../lib/mutation-error'
import { activityLabelKey, modeLabelKey } from '../../../lib/format'
import { draftActivities, hasDeparture } from '../../../stores/drafts'
import { useSharedOutlet } from '../shared-layout'
import { joinStepNeighbours, joinStepPath, useJoinDraft } from './layout'

export function JoinConfirmPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { shareToken, event } = useSharedOutlet()
  const { draft, update, reset } = useJoinDraft(shareToken)
  const participantQuery = useMyParticipant(shareToken)
  const joinEvent = useJoinEvent(shareToken)
  // Readable before membership exists, which is exactly when this step runs.
  const departuresQuery = useDepartureOptions(shareToken, true)
  const { previous } = joinStepNeighbours('confirm')

  // The summary is only meaningful once the draft reflects the server: on a
  // direct load the wizard seeds it in an effect, so rendering immediately would
  // show an empty draft as "unknown" for one frame.
  if (participantQuery.isPending) {
    return <LoadingBlock label={t('common.loading')} />
  }

  const { locationLat, locationLng } = draft
  const hasCoordinates =
    locationLat !== null && locationLng !== null && isGcj02Coordinate(locationLat, locationLng)
  // The summary and the request read the same activity set, so what the member
  // confirms is what the server stores: an empty selection means the whole event.
  const confirmedActivities = draftActivities(draft, event.activities)
  const isEdit = Boolean(participantQuery.data)
  // The departure point may be a kept group rather than coordinates, which is what
  // an edit that only changes a nickname or a headcount has.
  const departureNamed = hasDeparture(draft)
  // A first join has to name where it starts; an edit may keep whatever the
  // registration already has, including a point that was never set.
  const canSubmit = departureNamed || isEdit

  function submit(): void {
    if (!canSubmit) {
      return
    }

    const input: JoinEventInput = {
      nickname: draft.nickname.trim(),
      // A picked departure point is sent as an anchor; a member who insisted on
      // their exact spot sends coordinates instead, and the server resolves both
      // to the same stored anchor. An edit that names neither keeps the group it
      // already has, so nothing is sent for it.
      ...(draft.anchorId ? { anchorId: draft.anchorId } : {}),
      ...(hasCoordinates && locationLat !== null && locationLng !== null
        ? { locationLat, locationLng }
        : {}),
      ...(draft.headcount === 1 ? {} : { headcount: draft.headcount }),
      locationLabel: draft.locationLabel.trim() === '' ? null : draft.locationLabel.trim(),
      modes: draft.modes,
      attendingActivities: confirmedActivities,
      availableTimeStart: draft.availableTimeStart,
      availableTimeEnd: draft.availableTimeEnd,
      maxTravelMinutes: draft.maxTravelMinutes,
      budget: draft.budget,
      note: draft.note.trim() === '' ? null : draft.note.trim(),
      constraints:
        draft.dietaryAvoid.length === 0
          ? null
          : { avoid: draft.dietaryAvoid, allergy: [], require: [] },
      wants: draft.wants.length === 0 ? null : draft.wants,
      selfArrange: draft.selfArrangeAllowed ? { allowed: true, maxExtraMinutes: null, maxYuan: null } : null,
      // Travelling with an existing departure point is a declaration: the server
      // then uses that group's point instead of the one from the location step.
      ...(draft.travelGroupId ? { travelGroupId: draft.travelGroupId } : {}),
    }

    joinEvent.mutate(input, {
      onSuccess: () => {
        reset()
        navigate(`/e/${shareToken}`, { replace: true })
      },
    })
  }

  const options = departuresQuery.data?.options ?? []
  const errorText = mutationErrorText(joinEvent.error, t)
  // The group the member chose carries its own name; otherwise the draft label
  // names the point. Only the name is shown: raw coordinates are an input detail
  // the member typed once and gain nothing by being repeated here.
  const chosenGroup = options.find((option) => option.id === draft.travelGroupId) ?? null
  const departureName = chosenGroup?.label || draft.locationLabel || t('common.unknown')
  const timeWindow =
    draft.availableTimeStart || draft.availableTimeEnd
      ? `${draft.availableTimeStart ?? '—'} - ${draft.availableTimeEnd ?? '—'}`
      : t('common.unknown')

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardContent className="flex flex-col gap-4 pt-4 sm:pt-5">
          <StatGrid>
            <Stat label={t('join.nicknameLabel')} value={draft.nickname || t('common.unknown')} />
            <Stat
              label={t('join.headcountLabel')}
              value={t('join.headcountValue', { count: draft.headcount })}
            />
            <Stat
              label={t('join.locationLabel')}
              value={departureNamed ? departureName : t('common.unknown')}
            />
            <Stat
              label={t('join.stepModes')}
              value={
                draft.modes.length > 0
                  ? draft.modes.map((mode) => t(modeLabelKey(mode))).join(', ')
                  : t('common.unknown')
              }
            />
            <Stat label={t('join.stepTime')} value={timeWindow} />
            <Stat
              label={t('join.stepActivities')}
              value={
                confirmedActivities.length > 0
                  ? confirmedActivities
                      .map((activity) => t(activityLabelKey(activity)))
                      .join(', ')
                  : t('common.unknown')
              }
            />
            <Stat
              label={t('join.maxTravelLabel')}
              value={t('join.maxTravelUnit', { minutes: draft.maxTravelMinutes })}
            />
            <Stat label={t('budget.label')} value={t(`budget.${draft.budget}`)} />
            {/* A note nobody wrote is not an unknown value worth a row. */}
            {draft.note.trim() === '' ? null : (
              <Stat label={t('join.noteLabel')} value={draft.note} />
            )}
          </StatGrid>

          {!canSubmit ? (
            <Alert variant="destructive">
              <AlertDescription>
                {t('join.coordinateMissing')}{' '}
                <Link to={joinStepPath(shareToken, 'location')} className="underline">
                  {t('join.stepLocation')}
                </Link>
              </AlertDescription>
            </Alert>
          ) : null}

          {isEdit && !departureNamed ? (
            <Alert variant="info">
              <AlertDescription>
                {t('join.departureMissingEdit')}{' '}
                <Link to={joinStepPath(shareToken, 'location')} className="underline">
                  {t('join.stepLocation')}
                </Link>
              </AlertDescription>
            </Alert>
          ) : null}

          {errorText ? (
            <ErrorAlert message={errorText} />
          ) : null}
        </CardContent>
      </Card>

      {options.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">{t('join.travelWithTitle')}</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-2">
            <ChipGroup label={t('join.travelWithTitle')}>
              <Chip
                selected={draft.travelGroupId === null}
                onClick={() => update({ travelGroupId: null })}
              >
                {t('join.travelWithSelf')}
              </Chip>
              {options.map((option) => (
                <Chip
                  key={option.id}
                  selected={draft.travelGroupId === option.id}
                  onClick={() => update({ travelGroupId: option.id })}
                >
                  {t('join.travelWithOption', {
                    label: option.label,
                    count: option.headcount,
                  })}
                </Chip>
              ))}
            </ChipGroup>
            <p className="text-muted-foreground text-xs">{t('join.travelWithHint')}</p>
          </CardContent>
        </Card>
      ) : null}

      <WizardFooter
        backTo={previous ? joinStepPath(shareToken, previous) : undefined}
        backLabel={t('common.back')}
        onNext={submit}
        nextLabel={isEdit ? t('join.update') : t('join.submit')}
        nextDisabled={!canSubmit}
        busy={joinEvent.isPending}
      />

      <Button variant="ghost" size="sm" className="self-start" asChild>
        <Link to={`/e/${shareToken}`}>{t('overview.navOverview')}</Link>
      </Button>
    </div>
  )
}
